import { COR_PADRAO_DO_MARCADOR, corValida } from '@equiview/domain';
import { useState } from 'react';
import { ROTULO_VISTA } from '../dados/catalogo.js';
import type { DadosDaFoto, Midia, VistaDeFoto } from '../dados/fonte.js';
import { CorDoAdesivo } from './CorDoAdesivo.js';
import { FotoDaMidia } from './FotoDaMidia.js';
import { CampoTexto } from './ui/index.js';

const VISTAS: readonly VistaDeFoto[] = ['perfil_esquerdo', 'frente', 'tras'];

/** Ultima cor e distancia usadas neste aparelho: no dia de campo, mudam pouco. */
const CHAVE_COR = 'equiview.foto.cor';
const CHAVE_DISTANCIA = 'equiview.foto.distancia';
const ler = (chave: string) => {
  try {
    return localStorage.getItem(chave);
  } catch {
    return null;
  }
};
const lembrar = (chave: string, valor: string) => {
  try {
    localStorage.setItem(chave, valor);
  } catch {
    // Sem armazenamento (aba privada): so nao lembra.
  }
};

/** Abaixo disto a perspectiva aumenta as pernas (mais perto da camera) em relacao ao corpo. */
const DISTANCIA_MINIMA_M = 3;

/**
 * Estacao 2: as tres fotos da ficha. A de perfil esquerdo e a que mostra os
 * marcadores M1-M18 (angulos por fotogrametria) e vira a capa do animal.
 * A foto fica no aparelho e sobe quando houver rede.
 *
 * Tres colunas tambem no celular: miniaturas quadradas lado a lado, para a
 * estacao caber numa tela em vez de tres fotos vazias empilhadas. Embaixo, a cor
 * do adesivo e a distancia da camera, que a deteccao automatica usa.
 */
export function FotosDaEstacao2({
  midias,
  editavel,
  adicionar,
  remover,
  salvarDados,
}: {
  midias: readonly Midia[];
  editavel: boolean;
  adicionar: (vista: VistaDeFoto, arquivo: File, dados?: DadosDaFoto) => Promise<unknown>;
  remover: (id: string) => Promise<unknown>;
  salvarDados: (midiaId: string, dados: DadosDaFoto) => Promise<unknown>;
}) {
  const atualDe = (vista: VistaDeFoto) => midias.filter((m) => m.kind === 'foto' && m.view === vista).at(-1);
  const perfil = atualDe('perfil_esquerdo');
  const [cor, setCor] = useState(() => perfil?.marker_color ?? (corValida(ler(CHAVE_COR)) ? ler(CHAVE_COR)! : COR_PADRAO_DO_MARCADOR));
  const [distancia, setDistancia] = useState(() => (perfil?.camera_distance_m != null ? String(perfil.camera_distance_m).replace('.', ',') : (ler(CHAVE_DISTANCIA) ?? '')));
  const [erro, setErro] = useState<string | null>(null);

  const metros = distancia.trim() ? Number(distancia.replace(',', '.')) : null;
  const distanciaValida = metros === null || (Number.isFinite(metros) && metros > 0 && metros <= 30);
  const dadosDoPerfil = (): DadosDaFoto => ({ corDoMarcador: cor, ...(distanciaValida ? { distanciaM: metros } : {}) });

  const gravar = async (dados: DadosDaFoto) => {
    setErro(null);
    try {
      if (perfil) await salvarDados(perfil.id, dados);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <div className="space-y-4">
      <ul className="grid grid-cols-3 gap-2 sm:gap-4">
        {VISTAS.map((vista) => (
          <Vaga
            key={vista}
            vista={vista}
            atual={atualDe(vista)}
            editavel={editavel}
            adicionar={(arquivo) => adicionar(vista, arquivo, vista === 'perfil_esquerdo' ? dadosDoPerfil() : undefined)}
            remover={remover}
          />
        ))}
      </ul>

      {editavel && (
        <div className="space-y-3 rounded-(--radius-controle) border border-borda p-3">
          <p className="text-sm font-medium">Foto de perfil: adesivo e câmera</p>
          <p className="text-xs leading-snug text-apagado">
            Cavalo aprumado em chão plano. Câmera perpendicular ao lado esquerdo, na altura do meio do tronco, a 4–5 m, celular
            deitado e o corpo inteiro no quadro, sem zoom.
          </p>
          <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Cor do adesivo</p>
              <CorDoAdesivo
                valor={cor}
                onMudar={(hex) => {
                  setCor(hex);
                  lembrar(CHAVE_COR, hex);
                  void gravar({ corDoMarcador: hex });
                }}
              />
            </div>
            <CampoTexto
              rotulo="Distância da câmera (m)"
              inputMode="decimal"
              placeholder="4,5"
              value={distancia}
              onChange={(e) => setDistancia(e.target.value)}
              onBlur={() => {
                if (!distanciaValida) return;
                lembrar(CHAVE_DISTANCIA, distancia.trim());
                void gravar({ distanciaM: metros });
              }}
              erro={distanciaValida ? undefined : 'Entre 0 e 30 m.'}
              ajuda={metros !== null && distanciaValida && metros < DISTANCIA_MINIMA_M ? 'Perto demais: distorce as pernas. Afaste para 4–5 m.' : undefined}
            />
          </div>
          {erro && <p className="text-xs text-erro">{erro}</p>}
        </div>
      )}
    </div>
  );
}

function Vaga({
  vista,
  atual,
  editavel,
  adicionar,
  remover,
}: {
  vista: VistaDeFoto;
  atual: Midia | undefined;
  editavel: boolean;
  adicionar: (arquivo: File) => Promise<unknown>;
  remover: (id: string) => Promise<unknown>;
}) {
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);

  const escolher = async (arquivo: File | undefined) => {
    if (!arquivo) return;
    setErro(null);
    setGravando(true);
    try {
      // A nova primeiro: se ela for recusada (tamanho, tipo), a anterior fica.
      await adicionar(arquivo);
      if (atual) await remover(atual.id);
    } catch (e) {
      setErro(e instanceof Error ? e.message : String(e));
    } finally {
      setGravando(false);
    }
  };

  const situacao = !atual
    ? 'Sem foto'
    : atual.storage_path
      ? 'Enviada'
      : atual.pendente
        ? 'Neste aparelho, aguardando envio'
        : 'Tirada em outro aparelho, ainda não enviada';

  return (
    <li className="min-w-0 space-y-1.5 sm:space-y-2">
      <p className="text-[13px] leading-tight font-medium sm:text-sm">{ROTULO_VISTA[vista]}</p>
      <FotoDaMidia
        midia={atual}
        alt={ROTULO_VISTA[vista]}
        className="aspect-square w-full rounded-(--radius-controle) border border-borda sm:aspect-4/3"
      />
      <p className={`text-xs leading-snug ${atual?.storage_path ? 'text-ok' : 'text-apagado'}`}>{situacao}</p>
      {editavel && (
        <label
          className={`flex min-h-11 w-full cursor-pointer items-center justify-center rounded-(--radius-controle) border border-borda-forte bg-superficie px-2 text-[13px] font-medium select-none hover:border-destaque active:scale-[0.97] sm:inline-flex sm:w-auto sm:px-4 sm:text-sm ${
            gravando ? 'pointer-events-none opacity-60' : ''
          }`}
        >
          {gravando ? 'Guardando…' : atual ? 'Trocar foto' : 'Tirar foto'}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="sr-only"
            onChange={(e) => {
              void escolher(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
      )}
      {erro && <p className="text-xs text-erro">{erro}</p>}
    </li>
  );
}
